import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FaAngleDoubleRight } from 'react-icons/fa';
import { fetchUpdatePackageProgress, fetchInsurability, fetchUpdateInsurability } from '../../actions/EnrollmentActions';
import { ContentCard } from '../Cards';
import { Form } from '../../utils/FormTemplates';
import { userService } from '../../utils/UserService';
import { stepNumberHelper } from '../../utils/StepNumberHelper';
import ChangeNotification from '../Enrollment/ChangeNotification';

import '../../styles/components.scss';
import './Insurability.scss';
import { PackageStepType } from '../../types/api/enrollmentPackages/Package';

const currentStepName = "Insurability";

class Insurability extends Component {
    state = {
        person: null,
        personId: null
    };

    async componentDidMount() {
        const person = await userService.currentPerson();

        let personId = null;
        if (person) {
            // The full person is in state, but this helps simplify future logic.
            personId = person.personId;
        }
        this.setState({
            person: person,
            personId: personId
        }, () => {
            if (this.props.packageId) {
                return this.props.fetchInsurability(this.props.packageId);
            }
        });
    }

    onBack = () => {
        const activePackage = this.props.activePackage;

        if (activePackage) {
            const currentStepNumber = stepNumberHelper.stepNumberForNamedStep(activePackage, currentStepName);
            const previousStep = stepNumberHelper.getPreviousStep(activePackage, currentStepNumber);

            // This is a little hacky.
            // Basically: Check for the previous step. If the previous step has an entity id,
            // we are assuming it's a dynamic step that we can route to.
            // If it's not a dynamic step, we will hard code in a route to dependents.
            if (previousStep) {
                // Route to a dynamic step.
                if(previousStep.harmonyProductGroup) {
                    this.props.history.push(`/packages/${this.props.packageId}/harmony/${previousStep.harmonyProductGroup}`);
                } else if (previousStep.relatedEntityId) {
                    this.props.history.push(`/packages/${this.props.packageId}/plantypes/${previousStep.relatedEntityId}`);
                } else if (previousStep.type === PackageStepType.DocumentUpload) {
                    this.props.history.push(`/packages/${this.props.packageId}/documents`);
                }
                // Assume dependents is the previous step (this should also be dynamic in the future)
                else {
                    this.props.history.push(`/packages/${this.props.packageId}/dependents`);
                }
            }
        }
    };

    onNext = () => {
        if (this.props.packageId) {
            const currentStepNumber = stepNumberHelper.stepNumberForNamedStep(this.props.activePackage, currentStepName);
            this.props
                .fetchUpdatePackageProgress(this.props.packageId, currentStepNumber, true)
                .then(() => {
                    this.props.history.push(`/packages/${this.props.packageId}/summary`);
                });
        }
    };

    onCancel = () => {
        this.props.history.push(this.getPreviousStepRoute(this.props.activePackage));
    };

    getPreviousStepRoute = (activePackage) => {
        const previousStep = this.getNextStep(activePackage, -1);
        if (previousStep) {
            if (previousStep.type === 'PlanType' && previousStep.relatedEntityId) {
                if(previousStep.harmonyProductGroup) {
                    return '/packages/' + activePackage.packageId + '/harmony/' + previousStep.harmonyProductGroup;
                } else {
                    return '/packages/' + activePackage.enrollmentPackageId + '/plantypes/' + previousStep.relatedEntityId;
                }
            } else {
                return '/packages/' + activePackage.enrollmentPackageId + '/dependents';
            }
        } else {
            // if cannot determine next step, return current step
            return '/packages/' + activePackage.enrollmentPackageId + '/Insurability';
        }
    };

    getNextStep = (activePackage, direction) => {
        if (activePackage.steps) {
            for (let i = 0; i < activePackage.steps.length; i++) {
                if (activePackage.steps[i].type === 'Insurability') {
                    return activePackage.steps[i + direction];
                }
            }
        }
        return null;
    };

    addQuestionToSchema = (participant, question, schema, formData, requiredFields) => {
        // use the question id as the field property name
        const fieldName = question.id.toString();

        let fieldType = "";
        let fieldFormat = "";
        let fieldValue = undefined;

        // set field type, format and value based on response data type
        switch (question.responseDataType.toLowerCase()) {
            case 'boolean':
                fieldType = "boolean";
                if (question.response) {
                    fieldValue = question.response.valueBoolean;
                }
                break;
            case 'date':
                fieldType = "string";
                fieldFormat = "date";
                if (question.response) {
                    fieldValue = question.response.valueDate;
                }
                break;
            case 'number':
                fieldType = "string";
                if (question.response) {
                    fieldValue = question.response.valueNumber;
                }
                break;
            default:
                fieldType = "string";
                if (question.response) {
                    fieldValue = question.response.valueText;
                }
                break;
        }

        // set the question as a new property of the participant section
        schema.properties[participant.type].properties[fieldName] = {
            type: fieldType,
            format: fieldFormat,
            title: question.question,
            value: fieldValue
        };
        formData[participant.type][fieldName] = fieldValue;

        // if the question is required add it to the required fields array
        if (question.isRequired) {
            requiredFields.push(fieldName);
        }

        // add child questions
        if (question.children) {
            question.children.forEach(childQuestion => {
                this.addQuestionToSchema(participant, childQuestion, schema, formData, requiredFields);
            });
        }
    };

    addQuestionToUiSchema = (participant, question, uiSchema, fieldOrder) => {
        let fieldProperties = {};
        switch (question.responseDisplayType.toLowerCase()) {
            case 'date':
                break;
            case 'textarea':
                fieldProperties = {
                    "ui:widget": "textarea"
                };
                break;
            case 'yesno':
                fieldProperties = {
                    "ui:widget": "radio"
                };
                break;
            default: // number, text
                break;
        }
        uiSchema[participant.type][question.id] = fieldProperties;
        let fieldName = question.id.toString();
        fieldOrder.push(fieldName);

        // add child questions
        if (question.children) {
            question.children.forEach(childQuestion => {
                this.addQuestionToUiSchema(participant, childQuestion, uiSchema, fieldOrder);
            });
        }
    };

    buildSchemaAndData = (data) => {

        const schema = {
            title: "",
            type: "object",
            properties: {
            }
        };

        const formData = {};

        // create a section for each participant (Employee, Spouse, Child)
        data.participants.forEach(participant => {

            const requiredFields = [];

            // use the participant type as the property name
            schema.properties[participant.type] = {
                type: "object",
                title: participant.title,
                required: requiredFields,
                properties: {
                }
            };
            formData[participant.type] = {};

            // list each question for the participant
            participant.questions.forEach(question => {
                this.addQuestionToSchema(participant, question, schema, formData, requiredFields);
            });

            // set the required fields for this participant section
            schema.properties[participant.type].required = requiredFields;

        });

        return {
            schema: schema,
            formData: formData
        };
    };

    buildUiSchema = (data) => {
        const uiSchema = {};

        data.participants.forEach(participant => {

            uiSchema[participant.type] = {
            };

            var fieldOrder = [];

            participant.questions.forEach(question => {
                this.addQuestionToUiSchema(participant, question, uiSchema, fieldOrder);
            });

            uiSchema[participant.type]["ui:order"] = fieldOrder;

        });

        return uiSchema;
    };

    updateInsurability = async (e) => {
        if (this.props.activePackage.lastCompletedStepNumber === stepNumberHelper.stepNumberForNamedStep(this.props.activePackage, 'Finish')) {
            ChangeNotification();
        }
        const content = this.props.insurabilityContent.data;

        // for each participant property on the form
        for (let participantProperty in e.formData) {
            // find the matching participant in the InsurabilityContent DTO
            const participantIndex = content.participants.findIndex(p => p.type === participantProperty);
            const participant = content.participants[participantIndex];

            // for each question property on the form for this participant
            for (let questionProperty in e.formData[participantProperty]) {

                // find the matching question in the InsurabilityContent DTO
                let question = null;
                participant.questions.forEach(q => {
                    if (q.id.toString() === questionProperty) {
                        question = q;
                    }
                    // also search child questions, if any
                    if (!question && q.children) {
                        q.children.forEach(c => {
                            if (c.id.toString() === questionProperty) {
                                question = c;
                            }
                        });
                    }
                });

                if (question) {
                    // set a response value based on the question's response data type
                    switch (question.responseDataType.toLowerCase()) {
                        case 'boolean':
                            question.response = {
                                valueBoolean: e.formData[participantProperty][questionProperty]
                            };
                            break;
                        case 'date':
                            question.response = {
                                valueDate: e.formData[participantProperty][questionProperty]
                            };
                            break;
                        case 'number':
                            question.response = {
                                valueNumber: e.formData[participantProperty][questionProperty]
                            };
                            break;
                        default:
                            question.response = {
                                valueText: e.formData[participantProperty][questionProperty]
                            };
                            break;
                    }
                }
            }
        }

        const wasUpdateSuccessful =
            await this.props.fetchUpdateInsurability(this.props.packageId, content);

        if (wasUpdateSuccessful) {
            this.onNext();
        }
    };

    render() {
        const { data, error, loading } = this.props.insurabilityContent;

        let schemaAndData = null;
        let uiSchema = {};
        if (data) {
            schemaAndData = this.buildSchemaAndData(data);
            uiSchema = this.buildUiSchema(data);
        }

        return (
            <ContentCard loading={loading} error={error}>
                <h2>Evidence of Insurability</h2>

                {data && <div dangerouslySetInnerHTML={{ __html: data.enrollmentContent }} />}

                {schemaAndData &&
                    <div className="mt-4 mb-4 insurable">
                        <Form
                            formData={schemaAndData.formData}
                            schema={schemaAndData.schema}
                            uiSchema={uiSchema}
                            onSubmit={e => this.updateInsurability(e)}>
                            <div className="tc">
                                <button disabled={!this.props.activePackage} type="button" className="btn btn-default mr-2" onClick={e => this.onCancel(e)}>Back</button>
                                <div className="hide"><button type="submit" className="btn btn-primary">Next</button></div>
                                <button type="submit" disabled={!this.props.activePackage} className="btn btn-primary">Save &amp; Continue <FaAngleDoubleRight /></button>
                            </div>
                        </Form>
                    </div>
                }
            </ContentCard>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        insurabilityContent: state.enrollment.insurabilityContent,
        activePackage: state.packages.activePackage.data
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        fetchInsurability: (packageId) => {
            dispatch(fetchInsurability(packageId));
        },
        fetchUpdateInsurability: (packageId, insurability) => {
            return dispatch(fetchUpdateInsurability(packageId, insurability));
        },
        fetchUpdatePackageProgress: (packageId, stepNumber, updateSteps) => {
            return dispatch(fetchUpdatePackageProgress(packageId, stepNumber, updateSteps));
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Insurability));
